import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultArray } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';

import componentStyles from './pc-link.css.lit';
import globalStyles from './pc-link.lightDom.css.lit';

/**
 * Link component
 * @element pc-link
 * @slot - This element has a default slot
 */
@customElement('pc-link')
export class PCLink extends OutlineElement {
  adoptedStylesheets: AdoptedStyleSheets;
  static styles: CSSResultArray = [componentStyles];

  @property()
  size: 'sm' | 'base' | 'lg' = 'base';

  @property({ type: Boolean, attribute: 'is-reversed' })
  isReversed: false;

  @property()
  variant:
    | 'none'
    | 'default'
    | 'breadcrumb'
    | 'swipe-arrow'
    | 'nav'
    | 'icon'
    | 'icon-left'
    | 'icon-right' = 'default';

  @property({ type: Boolean })
  overlay = false;

  connectedCallback() {
    super.connectedCallback();
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);

    const classes = [
      'link',
      `link--size-${this.size}`,
      `link--${this.variant}`,
    ];

    if (this.isReversed) {
      classes.push('link--is-reversed');
    }

    if (this.overlay) {
      classes.push('link--overlay');
    }

    if (
      this.querySelector('pc-icon') &&
      this.variant !== 'icon-left' &&
      this.variant !== 'icon-right'
    ) {
      const children = this.querySelector('a')?.childNodes ?? [];
      const firstChildIsIcon = [...children][0].nodeName === 'PC-ICON';

      if (firstChildIsIcon) {
        classes.push(firstChildIsIcon ? 'link--icon-left' : 'link--icon-right');
      } else {
        const firstChildIsEmpty = [...children][0].textContent?.trim() === '';
        const secondChildIsIcon = [...children][1].nodeName === 'PC-ICON';

        classes.push(
          firstChildIsEmpty && secondChildIsIcon
            ? 'link--icon-left'
            : 'link--icon-right'
        );
      }
    }

    this?.querySelector('a')?.classList.add(...classes);
  }

  render(): TemplateResult {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-link': PCLink;
  }
}
